<template>
  <div
    class="card-container"
    :class="[`f-${theme}`, { 'is-pinned': pinned }]">
    <div class="card-header">
      <div class="card-header-icon">
        <svgicon :name="`ship/${activeKey}`" />
        <span class="level">{{ level + 1 }}</span>
      </div>
      <div class="card-header-content">
        <div class="title-large nowrap">
          {{ $t(`data.ship.${activeKey}.name`) }}
          <help-button :page="`ship/${activeKey}`" />
        </div>
        <div
          v-if="liveShip !== undefined && shipData.class == 'capital'"
          class="title-small">
          {{ liveShip.name }}
        </div>
      </div>
      <div
        v-if="pinned"
        class="card-close"
        v-tooltip="$t('card.close_hint')"
        @click="$emit('close')">
        <svgicon name="close" />
      </div>
    </div>

    <div class="card-body">
      <div class="card-illustration">
        <img
          v-if="!disabled"
          :src="`data/ships/${shipData.illustration}`" />
        <div
          v-else
          class="locked-item">
          <svgicon
            class="locked-icon"
            name="unlock" />
          <div
            v-html="disabled"
            class="locked-reason">
          </div>
        </div>

        <template v-if="!disabled">
          <svgicon
            v-for="(unit, i) in units"
            :key="`ship-unit-${i}`"
            v-tooltip="$t(`card.ship.hull_status`, { current: unit.hull, max: shipData.unit_hull })"
            class="ship-unit"
            name="ship/unit"
            :style="{
              'top': unit.position.x,
              'left': unit.position.y,
              'opacity': `${unit.status}`,
            }" />
        </template>

        <div class="toast is-bottom">
          <span>{{ morale }} {{ $t(`card.ship.morale`) }}</span>
        </div>
      </div>

      <div class="card-information">
        <div class="card-panel-controls">
          <div
            v-if="variants.length > 1"
            class="card-level-pips">
            <div
              v-for="variant in variants"
              :key="`formation-pip-${variant.key}`"
              class="card-level-pip"
              :class="{
                'is-active': variant.key === activeKey,
                'is-built': variant.key === shipKey,
              }"
              v-tooltip="formationTooltip(variant)"
              @click.stop="previewKey = variant.key">
              {{ variant.unit_count }}
              <span
                v-if="variant.key === bestVariantKey"
                class="pip-star">★</span>
            </div>
          </div>
        </div>

        <div class="card-panel-window">
          <div
            ref="panelContainer"
            class="card-panel-container">
            <div class="card-panel">
              <div class="is-sparse-y">
                <div>
                  <div
                    v-tooltip="$t(`card.ship.repair`)"
                    class="simple-bonus">
                    {{ shipData.unit_repair_coef * shipData.unit_count }}
                    <svgicon name="ship/repair" />
                  </div>
                  <div
                    v-tooltip="$t(`card.ship.bombing`)"
                    class="simple-bonus">
                    {{ shipData.unit_raid_coef * shipData.unit_count }}
                    <svgicon name="ship/raid" />
                  </div>
                  <div
                    v-tooltip="$t(`card.ship.invasion`)"
                    class="simple-bonus">
                    {{ shipData.unit_invasion_coef * shipData.unit_count }}
                    <svgicon name="ship/invasion" />
                  </div>
                </div>
                <div>
                  <div
                    v-tooltip="$t(`card.ship.maintenance`)"
                    class="simple-bonus">
                    {{ shipData.maintenance_cost | income(0) }}
                    <svgicon name="resource/credit" />
                  </div>
                </div>
              </div>

              <hr>

              <div class="ship-skills">
                <div class="skills-text">
                  <div>{{ $t(`card.ship.hull`) }}</div>
                  <div>
                    <strong>{{ shipData.unit_hull }}</strong>
                    <svgicon name="ship/hull" />
                  </div>
                </div>
                <div class="skills-line">
                  <span :style="width(Math.log(shipData.unit_hull) * 100, 1000)"></span>
                </div>
              </div>
              <div class="ship-skills">
                <div class="skills-text">
                  <div>{{ $t(`card.ship.maneuverability`) }}</div>
                  <div>
                    <strong>{{ formatPercent(unitHandling) }}</strong>
                    <svgicon name="ship/handling" />
                  </div>
                </div>
                <div class="skills-line">
                  <span :style="width(unitHandling, 100)"></span>
                </div>
              </div>
              <div class="ship-skills">
                <div class="skills-text">
                  <div>{{ $t(`card.ship.energy_strikes`) }}</div>
                  <div>
                    <strong>{{ formatList(unitEnergyStrikes) }}</strong>
                    <svgicon name="ship/energy_strikes" />
                  </div>
                </div>
                <div class="skills-line">
                  <span
                    v-for="(strike, i) in unitEnergyStrikes"
                    :key="`energy-strike-${shipKey}-${i}`"
                    :style="width(strike, 1300)">
                  </span>
                </div>
              </div>
              <div class="ship-skills">
                <div class="skills-text">
                  <div>{{ $t(`card.ship.explosive_strikes`) }}</div>
                  <div>
                    <strong>{{ formatList(unitExplosiveStrikes) }}</strong>
                    <svgicon name="ship/explosive_strikes" />
                  </div>
                </div>
                <div class="skills-line">
                  <span
                    v-for="(strike, i) in unitExplosiveStrikes"
                    :key="`explosive-strike-${shipKey}-${i}`"
                    :style="width(strike, 1100)">
                  </span>
                </div>
              </div>
              <div class="ship-skills">
                <div class="skills-text">
                  <div>{{ $t(`card.ship.shields`) }}</div>
                  <div>
                    <strong>{{ formatPercent(unitShield) }}</strong>
                    <svgicon name="ship/shield" />
                  </div>
                </div>
                <div class="skills-line">
                  <span :style="width(unitShield, 100)"></span>
                </div>
              </div>
              <div class="ship-skills">
                <div class="skills-text">
                  <div>{{ $t(`card.ship.flak`) }}</div>
                  <div>
                    <strong>{{ formatPercent(unitInterception) }}</strong>
                    <svgicon name="ship/interception" />
                  </div>
                </div>
                <div class="skills-line">
                  <span :style="width(unitInterception, 100)"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template>
      <div
        v-if="showCost || isPreviewingFormation"
        class="card-cost">
        <div
          class="icon-value"
          v-tooltip="$t('card.cost.production')">
          {{ shipData.production | integer }}
          <svgicon name="resource/production" />
          <template v-if="system">
            ({{ (shipData.production / system.production.value) * tickToSecondFactor | counter }})
          </template>
        </div>
        <div
          class="icon-value"
          v-tooltip="$t('card.cost.credit')">
          {{ shipData.credit_cost | integer }}
          <svgicon name="resource/credit" />
        </div>
        <div
          class="icon-value"
          v-tooltip="$t('card.cost.technology')">
          {{ shipData.technology_cost | integer }}
          <svgicon name="resource/technology" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import CardMixin from '@/game/mixins/CardMixin';
import HelpButton from '@/game/components/generic/HelpButton.vue';
import _groupBy from 'lodash/groupBy';

export default {
  components: { HelpButton },
  name: 'ship-card',
  mixins: [CardMixin],
  data() {
    return {
      previewKey: null,
    };
  },
  props: {
    shipKey: String,
    ship: {
      type: Object,
      required: false,
    },
    initialXP: {
      type: Number,
      default: 0,
    },
    system: {
      type: Object,
      required: false,
    },
    showCost: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: String,
      required: false,
    },
    pinned: {
      type: Boolean,
      default: false,
    },
    // rendered outside a game (portal battle simulator): read the portal's
    // global data and ignore whatever player the game store last held
    standalone: {
      type: Boolean,
      default: false,
    },
    // optional (shipData) => shipData hook, e.g. the simulator's balance
    // presets; applied to every formation the card shows
    tuneShip: {
      type: Function,
      required: false,
    },
  },
  computed: {
    tickToSecondFactor() { return this.$store.getters['game/tickToSecondFactor']; },
    globalData() { return this.standalone ? this.$store.state.portal.data : this.$store.state.game.data; },
    activeKey() { return this.previewKey === null ? this.shipKey : this.previewKey; },
    isPreviewingFormation() { return this.activeKey !== this.shipKey; },
    shipData() {
      const ship = this.globalData.ship.find((s) => s.key === this.activeKey);
      return this.tuneShip ? this.tuneShip(ship) : ship;
    },
    // formation pips: every squadron size this model comes in
    variants() {
      const { model } = this.globalData.ship.find((s) => s.key === this.shipKey);

      return this.globalData.ship
        .filter((s) => s.model === model)
        .sort((a, b) => a.unit_count - b.unit_count);
    },
    // a live ship's units/level only describe the card's own formation —
    // previewing another one must fall back to the blueprint stats
    liveShip() {
      return this.isPreviewingFormation ? undefined : this.ship;
    },
    playerPatents() {
      const { player } = this.$store.state.game;
      return !this.standalone && player && player.patents ? player.patents : null;
    },
    // the only formation the player can actually build: the largest
    // whose whole patent chain is owned (shipyards never build smaller
    // formations once a bigger one is unlocked)
    bestVariantKey() {
      if (!this.playerPatents) return null;

      let best = null;
      for (let i = 0; i < this.variants.length; i += 1) {
        const variant = this.variants[i];
        const owned = variant.patent === null || this.playerPatents.some((p) => p === variant.patent);
        if (!owned) break;
        best = variant;
      }

      return best ? best.key : null;
    },
    bestVariantUnitCount() {
      const best = this.variants.find((v) => v.key === this.bestVariantKey);
      return best ? best.unit_count : null;
    },
    experience() {
      return (this.liveShip === undefined)
        ? this.initialXP
        : this.liveShip.experience;
    },
    level() {
      if (this.liveShip === undefined) return 0;
      if (this.liveShip.level === 'hidden') return '?';
      return this.liveShip.level;
    },
    morale() {
      const constant = this.globalData.constant[0];
      return constant.army_unit_base_morale + (this.level * constant.army_unit_morale_per_level);
    },
    units() {
      return [...Array(this.shipData.unit_count).keys()].map((i) => {
        const position = { x: this.shipData.unit_pattern[i * 2], y: this.shipData.unit_pattern[(i * 2) + 1] };
        const hull = this.liveShip ? this.liveShip.units[i].hull : this.shipData.unit_hull;
        const status = hull / this.shipData.unit_hull;

        return { position, hull, status };
      });
    },
    unitHandling() {
      return this.liveShip
        ? Math.round(Math.min(this.shipData.unit_handling + (0.5 * this.liveShip.level), 95))
        : this.shipData.unit_handling;
    },
    unitInterception() {
      return this.liveShip && this.shipData.unit_interception > 0
        ? Math.round(Math.min(this.shipData.unit_interception + (0.5 * this.liveShip.level), 95))
        : this.shipData.unit_interception;
    },
    unitShield() {
      return this.liveShip && this.shipData.unit_shield > 0
        ? Math.round(Math.min(this.shipData.unit_shield + (0.5 * this.liveShip.level), 95))
        : this.shipData.unit_shield;
    },
    unitEnergyStrikes() {
      return this.liveShip
        ? this.shipData.unit_energy_strikes.map((s) => Math.round(s * (1 + 0.01 * this.liveShip.level)))
        : this.shipData.unit_energy_strikes;
    },
    unitExplosiveStrikes() {
      return this.liveShip
        ? this.shipData.unit_explosive_strikes.map((s) => Math.round(s * (1 + 0.01 * this.liveShip.level)))
        : this.shipData.unit_explosive_strikes;
    },
  },
  watch: {
    shipKey() { this.previewKey = null; },
  },
  methods: {
    formationTooltip(variant) {
      const n = variant.unit_count;

      // a real ship instance: its own pip states what it actually is
      if (this.ship && variant.key === this.shipKey) {
        return this.$t('card.ship.formation_current', { n });
      }

      if (!this.playerPatents) {
        return this.$t('card.ship.formation_preview', { n });
      }

      if (variant.key === this.bestVariantKey) {
        return this.$t('card.ship.formation_best', { n });
      }

      if (this.bestVariantUnitCount !== null && n < this.bestVariantUnitCount) {
        return this.$t('card.ship.formation_obsolete', { n });
      }

      return this.$t('card.ship.formation_locked', {
        n,
        patentName: this.$t(`data.patent.${variant.patent}.name`),
      });
    },
    width(value, maximum) {
      return `width: ${(value / maximum) * 100}%`;
    },
    formatPercent(value) {
      return value === 0 ? '—' : `${value}%`;
    },
    formatList(list) {
      const grouped = _groupBy(list, (n) => n);

      let string = '';
      Object.entries(grouped).forEach(([key, value]) => {
        string += value.length === 1
          ? `, ${key}`
          : `, ${value.length}×${key}`;
      });

      return string.length === 0
        ? '—'
        : string.substr(2);
    },
  },
};
</script>
